import moment from 'moment';
import { getFilterConfig, UUID_FILTER_KEYS, TYPE_DEPENDENT_FILTER_KEYS } from 'app/components/organisms/GlobalSearchModal/GlobalSearchModal.filterConfig';
import { isNullOperator } from 'app/components/organisms/GlobalSearchModal/GlobalSearchModal.constants';

// Operator mapping: UI symbol → Backend operator. UI shows != for user; query sends <>.
const OPERATOR_MAP = {
    // Number operators
    '=': '=',
    '!=': '<>',
    '<>': '<>',
    '>': '>',
    '<': '<',
    '>=': '>=',
    '<=': '<=',
    'between': 'between', // used internally for date range filters (not exposed in UI)
    
    // String operators
    '~': 'contains', // shorthand for contains
    '!~': 'not contains', // shorthand for not contains
    'contains': 'contains',
    'not contains': 'not contains',
    'like': 'like',
    'not like': 'not like',
    'ilike': 'ilike',
    'not ilike': 'not ilike',
    'starts with': 'starts with',
    'not starts with': 'not starts with',
    'ends with': 'ends with',
    'not ends with': 'not ends with',
    
    // Array operators (commented out - not currently supported)
    // 'contains properties': 'contains properties',
    // 'not contains properties': 'not contains properties',
    // 'is contained': 'is contained',
    // 'is not contained': 'is not contained',
    // 'overlaps': 'overlaps',
    // 'not overlaps': 'not overlaps',
    
    // Null operators
    'Ø': 'is null',
    '!Ø': 'is not null',
};

const FIELD_MAP = {
    type: 'type',
    createdBy: 'createdBy',
    modifiedBy: 'modifiedBy',
    createdDate: 'createdDate',
    modifiedDate: 'modifiedDate',
};

const DATE_FILTER_KEYS = ['createdDate', 'modifiedDate'];

const convertDateRangeToISO = (dateValue) => {
    if (!dateValue) {
        return null;
    }

    const dateRange = dateValue.dateRange || dateValue;

    if (Array.isArray(dateRange) && dateRange.length === 2) {
        return [
            moment(dateRange[0]).toISOString(),
            moment(dateRange[1]).toISOString()
        ];
    }

    if (dateRange.relative && dateRange.amount && dateRange.unit && dateRange.range) {
        const now = moment();
        let startDate, endDate;

        if (dateRange.range === 'subtract') {
            startDate = moment().subtract(dateRange.amount, dateRange.unit);
            endDate = now;
        } else if (dateRange.range === 'add') {
            startDate = now;
            endDate = moment().add(dateRange.amount, dateRange.unit);
        }

        return [
            startDate.toISOString(),
            endDate.toISOString()
        ];
    }

    return null;
};

/**
 * Derives the effective dataType from current search tokens.
 * Used to restrict dynamic (primary.*) filters to the same entity type when
 * a type filter or any primary.* filter is selected (query supports only one dataType).
 * Type filter takes precedence over primary.* (matches buildSearchCriteria).
 * @param {Array} searchTokens - Current search tokens
 * @returns {string|null} Entity type URI or null
 */
export const getEffectiveDataTypeFromTokens = (searchTokens) => {
    if (!searchTokens || searchTokens.length === 0) {
        return null;
    }

    let dataType = null;

    // Type filter takes precedence: use first type token with a value (skip Ø/!Ø)
    for (const token of searchTokens) {
        if (!token.filter || token.filter.key !== 'type') continue;
        if (isNullOperator(token.operator)) continue;
        if (token.value && typeof token.value === 'object') {
            dataType = token.value.type?.uri || token.value.uri || token.value;
        } else if (token.value) {
            dataType = token.value;
        }
        if (dataType) return dataType;
    }

    // Else use first primary.* filter's entity type
    for (const token of searchTokens) {
        if (!token.filter || !token.filter.key.startsWith('primary.')) continue;
        const config = getFilterConfig(token.filter.key);
        if (config?.entityTypeUri) return config.entityTypeUri;
    }

    return null;
};

/**
 * When any primary.* or type-dependent token (createdBy, modifiedBy, createdDate, modifiedDate)
 * exists, the Type token must not be removed (type is locked).
 * Returns the index of that Type token, or null if none.
 * @param {Array} searchTokens - Current search tokens
 * @returns {number|null} Index of locked Type token, or null
 */
export const getLockedTypeTokenIndex = (searchTokens) => {
    if (!searchTokens?.length) return null;
    const hasDependentFilter = searchTokens.some((t) =>
        t.filter?.key?.startsWith('primary.') ||
        TYPE_DEPENDENT_FILTER_KEYS.includes(t.filter?.key)
    );
    if (!hasDependentFilter) return null;
    const idx = searchTokens.findIndex((t) => t.filter?.key === 'type');
    return idx >= 0 ? idx : null;
};

export const buildSearchCriteria = (searchTokens) => {
    if (!searchTokens || searchTokens.length === 0) {
        return { dataType: null, text: null, filterBy: [] };
    }

    let dataType = null;
    let text = null;
    const filterBy = [];

    searchTokens.forEach(token => {
        if (!token.filter) {
            if (token.value) {
                // Combine multiple free text tokens with spaces
                text = text ? `${text} ${token.value}` : token.value;
            }
            return;
        }

        const filterKey = token.filter.key;
        
        if (filterKey === 'type') {
            if (isNullOperator(token.operator)) {
                const field = FIELD_MAP[filterKey] || filterKey;
                const op = OPERATOR_MAP[token.operator] || token.operator;
                filterBy.push({ field, op });
            } else {
                if (token.value && typeof token.value === 'object') {
                    dataType = token.value.type?.uri || token.value.uri || token.value;
                } else {
                    dataType = token.value;
                }
            }
            return;
        }

        const field = FIELD_MAP[filterKey] || filterKey;
        
        if (DATE_FILTER_KEYS.includes(filterKey)) {
            const dateRange = convertDateRangeToISO(token.value);
            if (dateRange) {
                filterBy.push({
                    field,
                    op: 'between',
                    value: dateRange
                });
            }
            return;
        }
        
        const op = OPERATOR_MAP[token.operator] || token.operator;

        if (isNullOperator(token.operator)) {
            filterBy.push({ field, op });
            return;
        }

        let value = token.value;

        const isUuidFilter = UUID_FILTER_KEYS.includes(filterKey);

        if (value && typeof value === 'object') {
            if (isUuidFilter) {
                value = Array.isArray(value)
                    ? value.map((v) => (v && (v.user?.id || v.id)) || null).filter(Boolean)
                    : value.user?.id || value.id;
            } else if (value.id) {
                value = value.id;
            }
        }

        if (value !== null && value !== undefined) {
            // For primary.* filters, set dataType at the query level (not inside filterBy)
            if (filterKey.startsWith('primary.')) {
                const filterConfig = getFilterConfig(filterKey);
                if (filterConfig?.entityTypeUri && !dataType) {
                    dataType = filterConfig.entityTypeUri;
                }
            }
            filterBy.push({ field, op, value });
        }
    });

    return { dataType, text, filterBy };
};

export const formatSearchResults = (results) => {
    if (!results || results.length === 0) {
        return [];
    }

    return results.map(result => {
        const entity = result.item;
        const typename = entity.__typename;
        
        const formatted = {
            id: entity.id,
            name: entity.name,
            type: entity.type || typename?.toLowerCase() || 'unknown',
            typename: typename,
            image: entity.image,
            iconName: entity.iconName,
            iconColor: entity.iconColor,
            iconType: entity.iconType,
            description: entity.description,
            active: entity.active,
            score: result.score,
        };

        if (typename === 'Task' || typename === 'Process' || typename === 'Entity' || typename === 'Workspace' || typename === 'Team') {
            formatted.createdDate = entity.createdDate;
            formatted.modifiedDate = entity.modifiedDate;
            formatted.createdBy = entity.createdBy;
            formatted.modifiedBy = entity.modifiedBy;
        }

        if (typename === 'Task' && entity.assignee) {
            formatted.assignee = entity.assignee;
        }

        if (typename === 'Entity') {
            formatted.primaryClass = entity.primaryClass;
            formatted.classes = entity.classes;
        }

        if (typename === 'Workspace' || typename === 'Team') {
            formatted.category = entity.category;
            formatted.isSystem = entity.isSystem;
        }

        return formatted;
    });
};

